import React, { useState, useEffect } from 'react';
import { Calendar, TrendingUp, TrendingDown, Save, Plus, Trash2 } from 'lucide-react';
import { usePricing, EventTypeMultiplier } from '@context/PricingContext';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';

interface EventTypeMultipliersProps {
  onDataChange?: () => void;
  onSaveSuccess?: () => void;
}

const EventTypeMultipliers: React.FC<EventTypeMultipliersProps> = ({
  onDataChange,
  onSaveSuccess,
}) => {
  const { configuration, loading, updateEventMultipliers } = usePricing();
  const [multipliers, setMultipliers] = useState<EventTypeMultiplier[]>([
    { eventType: 'wedding', multiplier: 1.5, displayName: 'Wedding' },
    { eventType: 'corporate', multiplier: 1.3, displayName: 'Corporate' },
    { eventType: 'birthday', multiplier: 1.0, displayName: 'Birthday' },
    { eventType: 'sports', multiplier: 0.9, displayName: 'Sports' },
  ]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (configuration?.eventTypeMultipliers) {
      setMultipliers([...configuration.eventTypeMultipliers]);
    }
  }, [configuration?.eventTypeMultipliers]);

  const handleMultiplierChange = (
    index: number,
    field: keyof EventTypeMultiplier,
    value: string
  ) => {
    const updatedMultipliers = multipliers.map((mult, i) => {
      if (i === index) {
        if (field === 'multiplier') {
          const numericValue = Math.max(0.1, Math.min(5.0, parseFloat(value) || 1.0));
          return { ...mult, [field]: numericValue };
        } else {
          return { ...mult, [field]: value };
        }
      }
      return mult;
    });

    setMultipliers(updatedMultipliers);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const addMultiplier = () => {
    const newMultiplier: EventTypeMultiplier = {
      eventType: 'custom',
      multiplier: 1.0,
      displayName: 'Custom Event',
    };

    setMultipliers([...multipliers, newMultiplier]);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const removeMultiplier = (index: number) => {
    const updatedMultipliers = multipliers.filter((_, i) => i !== index);
    setMultipliers(updatedMultipliers);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(false);

    // Validate multipliers
    const errors = [];
    const eventTypes = multipliers.map(m => m.eventType.toLowerCase());
    const uniqueEventTypes = new Set(eventTypes);

    if (eventTypes.length !== uniqueEventTypes.size) {
      errors.push('Event types must be unique');
    }

    if (multipliers.some(m => !m.eventType.trim())) {
      errors.push('All event types must have a name');
    }

    if (multipliers.some(m => !m.displayName.trim())) {
      errors.push('All event types must have a display name');
    }

    if (multipliers.some(m => m.multiplier < 0.1 || m.multiplier > 5.0)) {
      errors.push('Multipliers must be between 0.1 and 5.0');
    }

    if (errors.length > 0) {
      setSaveError(errors.join('. '));
      setSaveLoading(false);
      return;
    }

    try {
      await updateEventMultipliers(multipliers);
      setSaveSuccess(true);
      onSaveSuccess?.();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save event multipliers');
    } finally {
      setSaveLoading(false);
    }
  };

  const getMultiplierIcon = (multiplier: number) => {
    if (multiplier > 1.0) {
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    } else if (multiplier < 1.0) {
      return <TrendingDown className="h-4 w-4 text-red-600" />;
    }
    return <div className="h-4 w-4 bg-gray-400 rounded-full" />;
  };

  const getMultiplierColor = (multiplier: number): string => {
    if (multiplier > 1.2) return 'text-green-700 bg-green-50 border-green-200';
    if (multiplier > 1.0) return 'text-blue-700 bg-blue-50 border-blue-200';
    if (multiplier < 0.9) return 'text-red-700 bg-red-50 border-red-200';
    return 'text-gray-700 bg-gray-50 border-gray-200';
  };

  const calculateExamplePrice = (multiplier: number): string => {
    if (!configuration?.basePrices) return '$0.00';

    const basePrice = configuration.basePrices.singlePhoto;
    const adjustedPrice = basePrice * multiplier;
    return `$${(adjustedPrice / 100).toFixed(2)}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Calendar className="h-5 w-5 text-purple-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Event Type Pricing</h2>
          </div>
          <Button
            variant="ghost"
            size="small"
            onClick={addMultiplier}
            icon={<Plus className="h-4 w-4" />}
          >
            Add Event Type
          </Button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Adjust pricing based on event types and their typical value.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {saveError && <Alert type="error" title="Save Failed" message={saveError} />}

        {saveSuccess && (
          <Alert
            type="success"
            title="Saved Successfully"
            message="Your event type pricing has been updated."
          />
        )}

        <div className="space-y-4">
          {multipliers.map((multiplier, index) => (
            <div
              key={`${multiplier.eventType}-${index}`}
              className={`border rounded-lg p-4 ${getMultiplierColor(multiplier.multiplier)}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                <div>
                  <Input
                    label="Event Type"
                    type="text"
                    value={multiplier.eventType}
                    onChange={e => handleMultiplierChange(index, 'eventType', e.target.value)}
                    placeholder="wedding"
                    helperText="Internal identifier"
                  />
                </div>

                <div>
                  <Input
                    label="Display Name"
                    type="text"
                    value={multiplier.displayName}
                    onChange={e => handleMultiplierChange(index, 'displayName', e.target.value)}
                    placeholder="Wedding"
                    helperText="Customer-facing name"
                  />
                </div>

                <div>
                  <Input
                    label="Price Multiplier"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="5.0"
                    value={multiplier.multiplier.toString()}
                    onChange={e => handleMultiplierChange(index, 'multiplier', e.target.value)}
                    placeholder="1.0"
                    helperText={
                      multiplier.multiplier === 1.0
                        ? 'Standard pricing'
                        : multiplier.multiplier > 1.0
                          ? `+${((multiplier.multiplier - 1) * 100).toFixed(0)}%`
                          : `${((1 - multiplier.multiplier) * 100).toFixed(0)}% off`
                    }
                  />
                </div>

                <div className="text-sm">
                  <div className="font-medium text-gray-700">Example Price</div>
                  <div className="flex items-center">
                    {getMultiplierIcon(multiplier.multiplier)}
                    <span className="ml-1 font-semibold">
                      {calculateExamplePrice(multiplier.multiplier)}
                    </span>
                  </div>
                  <div className="text-gray-600 text-xs">per photo</div>
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => removeMultiplier(index)}
                    icon={<Trash2 className="h-4 w-4" />}
                    className="text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {multipliers.length === 0 && (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No event types configured</p>
            <Button
              variant="outline"
              onClick={addMultiplier}
              className="mt-4"
              icon={<Plus className="h-4 w-4" />}
            >
              Add First Event Type
            </Button>
          </div>
        )}

        {/* Pricing Guidelines */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="font-medium text-yellow-900 mb-2">Pricing Guidelines</h3>
          <div className="text-sm text-yellow-700 space-y-1">
            <div>
              <strong>Premium Events (1.3-1.5x):</strong> Weddings, corporate galas, formal
              celebrations
            </div>
            <div>
              <strong>Standard Events (1.0x):</strong> Birthdays, parties, casual celebrations
            </div>
            <div>
              <strong>Volume Events (0.8-0.9x):</strong> Sports, school events, high photo counts
            </div>
          </div>
        </div>

        {multipliers.length > 0 && (
          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              loading={saveLoading}
              disabled={loading}
              icon={<Save className="h-4 w-4" />}
            >
              Save Event Types
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventTypeMultipliers;
